import { X, Calendar, User, FileText, Lightbulb, ExternalLink, Download } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { InnoFlash, categoryConfig } from '@/data/innoflashes';
import { dataTeamServices } from '@/data/dataTeamServices';
import { useNavigate } from 'react-router-dom';

interface InnoFlashModalProps {
  flash: InnoFlash | null;
  isOpen: boolean;
  onClose: () => void;
}

export function InnoFlashModal({ flash, isOpen, onClose }: InnoFlashModalProps) {
  const navigate = useNavigate();

  if (!isOpen || !flash) return null;

  const categoryStyle = categoryConfig[flash.category];

  // Get related tools
  const relatedTools = flash.relatedTools
    ? dataTeamServices.filter((service) => flash.relatedTools?.includes(service.id))
    : [];

  const handleToolClick = (serviceId: string) => {
    onClose();
    navigate('/services');
    // Small delay to ensure navigation completes before scrolling
    setTimeout(() => {
      const element = document.getElementById(`service-${serviceId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        <div
          className="bg-white rounded-xl max-w-4xl max-h-[90vh] w-full overflow-hidden flex flex-col"
          style={{
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            className="relative p-6 border-b border-[#E5E7EB]"
            style={{
              backgroundColor: categoryStyle.bgColor,
            }}
          >
            <button
              onClick={onClose}
              className="absolute right-4 top-4 p-2 rounded-lg hover:bg-white/50 transition-colors"
              aria-label="Close modal"
            >
              <X size={24} className="text-sb-dark-grey" />
            </button>

            <Badge
              className="mb-3"
              style={{
                backgroundColor: categoryStyle.bgColor,
                color: categoryStyle.color,
                border: `1px solid ${categoryStyle.color}40`,
                fontSize: '11px',
                fontWeight: '500',
              }}
            >
              {categoryStyle.label}
            </Badge>

            <h2
              id="modal-title"
              className="text-2xl font-bold text-sb-black pr-12 leading-tight"
            >
              InnoFlash #{flash.id}: {flash.title}
            </h2>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Preview Image */}
            {flash.previewImage && (
              <div className="mb-6 rounded-lg overflow-hidden bg-[#F3F4F6]">
                <img
                  src={flash.previewImage}
                  alt={flash.title}
                  className="w-full h-auto object-contain max-h-96"
                />
              </div>
            )}

            {/* Metadata */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="flex items-center gap-2 text-sm text-sb-dark-grey">
                <Calendar size={16} />
                <span>
                  {new Date(flash.date).toLocaleDateString('nl-NL', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm text-sb-dark-grey">
                <User size={16} />
                <span>{flash.author}</span>
              </div>
              {flash.imagesAvailable > 0 && (
                <div className="flex items-center gap-2 text-sm text-sb-dark-grey">
                  <FileText size={16} />
                  <span>{flash.imagesAvailable} visualizaties beschikbaar</span>
                </div>
              )}
              {flash.wordCount > 0 && (
                <div className="flex items-center gap-2 text-sm text-sb-dark-grey">
                  <FileText size={16} />
                  <span>{flash.wordCount.toLocaleString()} woorden</span>
                </div>
              )}
            </div>

            {/* PDF Download */}
            {flash.pdfPath && (
              <div className="mb-6">
                <Button
                  asChild
                  variant="outline"
                  className="inline-flex border-[#e85a84] text-[#b1466b] hover:bg-[#FDE6E3]"
                >
                  <a href={flash.pdfPath} download target="_blank" rel="noreferrer">
                    <Download size={16} />
                    Download Full PDF
                    {typeof flash.pdfSizeM === 'number' && (
                      <span className="ml-1">
                        ({flash.pdfSizeM.toFixed(2)} MB)
                      </span>
                    )}
                  </a>
                </Button>
              </div>
            )}

            {/* Description */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-sb-black mb-3">Beschrijving</h3>
              <p className="text-base text-sb-dark-grey leading-relaxed whitespace-pre-line">{flash.fullDescription}</p>
            </div>

            {/* Methodology */}
            {flash.methodology && (
              <div className="mb-6 rounded-lg bg-gray-50 p-4">
                <h4 className="mb-2 text-sm font-semibold text-gray-700">Methodology</h4>
                <p className="text-sm leading-relaxed text-gray-600">{flash.methodology}</p>
              </div>
            )}

            {/* Real-World Example */}
            {flash.realWorldExample && (
              <div className="mb-6 rounded-lg bg-blue-50 p-4 border border-blue-200">
                <h4 className="mb-2 text-sm font-semibold text-blue-700">Real-World Application</h4>
                <p className="text-sm leading-relaxed text-blue-600">{flash.realWorldExample}</p>
              </div>
            )}

            {/* Additional Images */}
            {flash.additionalImages && flash.additionalImages.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-sb-black mb-3">Additional Images</h3>
                <div className="grid grid-cols-2 gap-4">
                  {flash.additionalImages.map((img, idx) => (
                    <div key={idx} className="rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                      <img
                        src={img}
                        alt={`${flash.title} example ${idx + 1}`}
                        className="w-full h-auto object-contain"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tags */}
            {flash.tags.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-sb-black mb-3">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {flash.tags.map((tag) => (
                    <Badge
                      key={tag}
                      variant="secondary"
                      className="text-xs"
                      style={{
                        backgroundColor: '#F3F4F6',
                        color: '#6B7280',
                        fontSize: '11px',
                        fontWeight: '500',
                      }}
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Related Tools */}
            {relatedTools.length > 0 && (
              <div className="rounded-lg border border-[#A5CE39] bg-[#F7FAE9] p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Lightbulb size={18} className="text-[#A5CE39]" />
                  <h3 className="text-base font-semibold text-[#7A9C2F]">
                    Related Data Team Services
                  </h3>
                </div>
                <p className="text-sm text-[#5C7524] mb-3">
                  Related Service: request from the data team to get this methodology applied.
                </p>
                <div className="space-y-2">
                  {relatedTools.map((tool) => (
                    <button
                      key={tool.id}
                      onClick={() => handleToolClick(tool.id)}
                      className="flex w-full items-start gap-3 rounded-md border border-[#C0DB77] bg-white p-3 text-left transition-all hover:border-[#A5CE39] hover:shadow-sm"
                    >
                      <Lightbulb size={16} className="mt-0.5 flex-shrink-0 text-[#A5CE39]" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-sb-black">
                          Related Service: {tool.title}
                        </p>
                        <p className="text-xs text-[#5C7524]">Request from data team →</p>
                        <p className="mt-1 text-xs text-sb-grey line-clamp-2">
                          {tool.tagline}
                        </p>
                      </div>
                      <ExternalLink size={14} className="mt-0.5 flex-shrink-0 text-sb-grey" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-[#E5E7EB] flex justify-end">
            <Button
              onClick={onClose}
              variant="outline"
              className="px-6 py-2 text-sm font-medium"
            >
              Sluiten
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
